import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuTextMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { $createTextNode, $getSelection, $insertNodes, $isRangeSelection, TextNode } from 'lexical';
import { Fragment, ReactNode, useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Typography } from '@mui/material';
import { arity, BUILTINS, FormulaFunctionName, FunctionSpec } from '../../formula-engine';
import { $enclosingCallsAtCaret, BoundName, EnclosingCall, namesInScope, slotKind } from './enclosingCalls';
import { $displayOffsetOf, $serializeFormula } from './serialization';
import { isInsideStringLiteral } from './StringLiteralPlugin';
import { TypeaheadMenu } from './TypeaheadMenu';

/** The registry entry behind a name the editor is showing; names come from the registry, so this holds. */
function spec(name: FormulaFunctionName): FunctionSpec {
  const entry = BUILTINS.get(name);
  if (!entry) {
    throw new Error(`Unknown function "${name}"`);
  }
  return entry;
}

/** The signature line as users see it, e.g. "ROUND(value, digits?)". */
export function functionSignature(name: FormulaFunctionName): string {
  return spec(name).signature;
}

/** The one-line doc the typeahead's side panel shows. */
export function functionDoc(name: FormulaFunctionName): string {
  return spec(name).doc;
}

/**
 * What selecting a function inserts. Zero-arg calls come completed. A trailing
 * "[" opens the field typeahead right away, but only where nothing else can
 * start the first argument (SIZE, INDEX, SUM, KPI) — elsewhere an expression
 * is just as likely, e.g. IF(MAX(…)), and the "[" would only be in the way.
 */
export function functionInsertText(name: FormulaFunctionName): string {
  if (arity(spec(name)).max === 0) {
    return `${name}()`;
  }
  return slotKind({ fn: name, argIndex: 0, args: [] }) === 'field' ? `${name}([` : `${name}(`;
}

/**
 * Tail matters: the trailing "[" only serves an empty slot. "" (or
 * whitespace), ")" and "," after the caret are empty slots; anything else is
 * existing content a "[" would corrupt, so the bare call goes in.
 */
export function insertTextForTail(name: FormulaFunctionName, tail: string): string {
  const text = functionInsertText(name);
  if (!text.endsWith('[')) {
    return text;
  }
  const ahead = tail.trimStart();
  return ahead === '' || ahead.startsWith(')') || ahead.startsWith(',') ? text : text.slice(0, -1);
}

/**
 * The completion the caret's surroundings allow. Call inside editor.update,
 * before the insertion mutates the tree; the caret sits at the end of the
 * typed query (or the neutral insert point), so the display text after it is
 * exactly what will follow the insertion.
 */
export function $functionInsertText(name: FormulaFunctionName): string {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
    // No caret to inspect: an unseen tail could be content, so stay bare.
    return functionInsertText(name).replace(/\[$/, '');
  }
  const caret = $displayOffsetOf(selection.anchor.getNode(), selection.anchor.offset);
  return insertTextForTail(name, $serializeFormula().display.slice(caret));
}

const FUNCTION_NAMES = BUILTINS.names();

/**
 * A trailing identifier opens the menu — but not inside an unclosed field ref,
 * where the field typeahead owns the caret. A whole identifier, digits and
 * underscores included, so a bound name like net_1 keeps completing past its
 * first non-letter.
 */
export function matchFunctionTrigger(text: string): MenuTextMatch | null {
  if (isInsideStringLiteral(text) || /\[[^\][]*$/.test(text)) {
    return null;
  }
  const match = /([A-Za-z_][A-Za-z0-9_]*)$/.exec(text);
  if (!match) {
    return null;
  }
  return { leadOffset: match.index, matchingString: match[1], replaceableString: match[1] };
}

/**
 * Every function the caret's position allows, before any prefix filtering: a
 * slot narrow enough to name its one kind offers only what parses there —
 * nothing at all where a chip is the only thing that does, LAMBDA alone as
 * REDUCE's last argument (and nowhere else).
 */
export function offeredFunctions(calls: EnclosingCall[]): FormulaFunctionName[] {
  const kind = slotKind(calls[0]);
  if (kind === 'field') {
    return [];
  }
  return kind === 'lambda' ? ['LAMBDA'] : FUNCTION_NAMES.filter((name) => name !== 'LAMBDA');
}

/**
 * Case-insensitive prefix match; an exact full name (e.g. "IF") still lists
 * itself.
 */
export function filterFunctionOptions(query: string, calls: EnclosingCall[] = []): FormulaFunctionName[] {
  const q = query.trim().toUpperCase();
  if (!q) {
    return [];
  }
  return offeredFunctions(calls).filter((name) => name.startsWith(q));
}

/**
 * The bound names the caret's position allows, prefix-matched like functions:
 * every name in scope wherever an expression parses, none in a slot that only
 * takes a chip or a LAMBDA.
 */
export function filterNameOptions(query: string, calls: EnclosingCall[] = []): BoundName[] {
  const q = query.trim().toUpperCase();
  if (!q || slotKind(calls[0]) !== 'free') {
    return [];
  }
  return namesInScope(calls).filter((bound) => bound.name.toUpperCase().startsWith(q));
}

/** Doc-panel content for a bound name: the name and which binder it belongs to. */
export function renderNameDetail(bound: BoundName): ReactNode {
  return (
    <>
      <Typography variant='caption' component='div' sx={{ fontWeight: 700 }} color='text.primary'>
        {bound.name}
      </Typography>
      <Typography variant='caption' component='div' color='text.secondary' sx={{ mt: 0.5 }}>
        {bound.binder === 'LET'
          ? 'A name bound by LET — the value it was given.'
          : 'The running value REDUCE carries through its list.'}
      </Typography>
    </>
  );
}

/**
 * The parameters of a signature, split on its top-level commas: "[...]" spans
 * and nested calls hold together, so REDUCE's LAMBDA(acc, expression) counts
 * as the one parameter it is.
 */
export function signatureParams(name: FormulaFunctionName): string[] {
  const signature = functionSignature(name);
  const inner = signature.slice(signature.indexOf('(') + 1, signature.lastIndexOf(')'));
  const params: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < inner.length; i++) {
    const ch = inner[i];
    if (ch === '(' || ch === '[') {
      depth++;
    } else if (ch === ')' || ch === ']') {
      depth--;
    } else if (ch === ',' && depth === 0) {
      params.push(inner.slice(start, i).trim());
      start = i + 1;
    }
  }
  const last = inner.slice(start).trim();
  return last ? [...params, last] : params;
}

/**
 * The parameter an argument position highlights. A variadic call keeps
 * highlighting its "…" however many arguments follow the named ones; a
 * fixed-arity call already holding too many highlights nothing, and neither
 * does one that takes no arguments at all. LET's first name and value are
 * certain; from the third argument on the caret cannot tell a further name,
 * its value or the calculation apart (Excel's own hint has the same limit),
 * so "…" stays lit and the doc line says how they alternate. DATEADD's pairs
 * are certain, so amount and unit take turns however many follow the date.
 */
export function activeParamIndex(name: FormulaFunctionName, argIndex: number): number | null {
  const params = signatureParams(name);
  if (argIndex < 0 || params.length === 0) {
    return null;
  }
  if (name === 'LET') {
    return Math.min(argIndex, params.indexOf('…'));
  }
  if (name === 'DATEADD' && argIndex > 0) {
    // Amount and unit alternate for as long as the pairs go on.
    return 1 + ((argIndex - 1) % 2);
  }
  if (argIndex < params.length) {
    return argIndex;
  }
  return arity(spec(name)).max === Infinity ? params.length - 1 : null;
}

/** The signature line with one parameter picked out, in the editor's own function colour. */
function renderSignature(name: FormulaFunctionName, activeIndex: number): ReactNode {
  return (
    <>
      {`${name}(`}
      {signatureParams(name).map((param, index) => (
        <Fragment key={param}>
          {index > 0 && ', '}
          {index === activeIndex ? (
            <Box component='span' sx={{ color: 'var(--mui-colors-primary-50)', textDecoration: 'underline' }}>
              {param}
            </Box>
          ) : (
            param
          )}
        </Fragment>
      ))}
      {')'}
    </>
  );
}

/**
 * Doc-panel content for a function: signature and one-line doc. Given the
 * argument the caret is in (the signature hint) that parameter stands out;
 * without one — menu rows, hover docs, Ctrl+Space — the signature reads plain.
 */
export function renderFunctionDetail(name: FormulaFunctionName, activeIndex?: number | null): ReactNode {
  return (
    <>
      <Typography variant='caption' component='div' sx={{ fontWeight: 700 }} color='text.primary'>
        {activeIndex == null ? functionSignature(name) : renderSignature(name, activeIndex)}
      </Typography>
      <Typography variant='caption' component='div' color='text.secondary' sx={{ mt: 0.5 }}>
        {functionDoc(name)}
      </Typography>
    </>
  );
}

type FunctionMenuChoice =
  { kind: 'function'; name: FormulaFunctionName } | { kind: 'name'; bound: BoundName };

class FunctionMenuOption extends MenuOption {
  constructor(readonly choice: FunctionMenuChoice) {
    super(choice.kind === 'function' ? `fn:${choice.name}` : `name:${choice.bound.name}`);
  }
}

/** The row for a choice; names lead, since inside a body they are the likelier completion. */
export function functionMenuItem(choice: FunctionMenuChoice) {
  return choice.kind === 'function'
    ? {
        key: `fn:${choice.name}`,
        primary: choice.name,
        badge: 'fn' as const,
        detail: renderFunctionDetail(choice.name),
      }
    : {
        key: `name:${choice.bound.name}`,
        primary: choice.bound.name,
        badge: 'n' as const,
        detail: renderNameDetail(choice.bound),
      };
}

export function FunctionTypeaheadPlugin({ onOpenChange }: { onOpenChange?: (open: boolean) => void }) {
  const [editor] = useLexicalComposerContext();
  const [query, setQuery] = useState<string | null>(null);
  const [enclosingCalls, setEnclosingCalls] = useState<EnclosingCall[]>([]);

  // See FieldTypeaheadPlugin: a null query renders nothing and reports closed.
  const handleQueryChange = useCallback(
    (nextQuery: string | null) => {
      setQuery(nextQuery);
      if (nextQuery === null) {
        onOpenChange?.(false);
        return;
      }
      const calls = editor.getEditorState().read(() => $enclosingCallsAtCaret());
      setEnclosingCalls(calls);
    },
    [editor, onOpenChange]
  );

  const options = useMemo(
    () =>
      query === null
        ? []
        : [
            ...filterNameOptions(query, enclosingCalls).map(
              (bound) => new FunctionMenuOption({ kind: 'name', bound })
            ),
            ...filterFunctionOptions(query, enclosingCalls).map(
              (name) => new FunctionMenuOption({ kind: 'function', name })
            ),
          ],
    [query, enclosingCalls]
  );

  const onSelectOption = useCallback(
    (option: FunctionMenuOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
      editor.update(() => {
        const { choice } = option;
        const node = $createTextNode(
          choice.kind === 'function' ? $functionInsertText(choice.name) : choice.bound.name
        );
        if (nodeToReplace) {
          nodeToReplace.replace(node);
        } else {
          $insertNodes([node]);
        }
        node.select();
        closeMenu();
      });
    },
    [editor]
  );

  const triggerFn = useCallback((text: string) => matchFunctionTrigger(text), []);

  return (
    <LexicalTypeaheadMenuPlugin<FunctionMenuOption>
      onQueryChange={handleQueryChange}
      onSelectOption={onSelectOption}
      triggerFn={triggerFn}
      // Function names are text entities; "[" or a name typed right after one
      // ("IF([", "REDUCE(ro") must still open the menus.
      ignoreEntityBoundary
      options={options}
      onOpen={() => onOpenChange?.(true)}
      onClose={() => onOpenChange?.(false)}
      menuRenderFn={(anchorRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) =>
        anchorRef.current && options.length
          ? createPortal(
              <TypeaheadMenu
                options={options}
                toItem={(option) => functionMenuItem(option.choice)}
                selectedIndex={selectedIndex}
                onHighlight={setHighlightedIndex}
                onSelect={selectOptionAndCleanUp}
              />,
              anchorRef.current
            )
          : null
      }
    />
  );
}
